/**
 * Defensive wrapper for Grafana Faro error reporting.
 * Reuses Logger.appMetadata to maintain parity with Splunk logging.
 * All operations are wrapped in try-catch to prevent app breakage.
 * Tracks integration health telemetry to Splunk.
 */

import { faro } from '@grafana/faro-web-sdk';
import { SESSION_ID_STORAGE_KEY } from '../constants';
import { LogAction } from '../constants/LogActions';
import { Logger } from '../Logger';
import { EventType } from '../models';

export interface FaroErrorContext {
  componentStack?: string | null;
  errorMessage?: string;
  errorSource?: string;
  isExternalUser: boolean;
  type: 'fatal-frontend-error';
  // Axios error details
  apiMethod?: string;
  apiUrl?: string;
  requestId?: string;
  responseData?: string;
  responseStatus?: number;
}

/**
 * Reports a fatal frontend error to Grafana Faro with defensive error handling.
 * Enriches context with Logger.appMetadata and tracks telemetry to Splunk.
 *
 * @param error - Error object to report
 * @param context - Error context from error boundary
 */
export function reportFatalErrorToFaro(error: Error, context: FaroErrorContext): void {
  try {
    if (!faro?.api?.pushError) {
      trackFaroIntegrationFailure('faro_api_unavailable', {
        errorMessage: error.message,
        errorSource: context.errorSource,
      });
      if (import.meta.env.DEV) {
        console.warn('[FaroErrorReporter] Faro API unavailable');
      }
      return;
    }

    const enrichedContext = {
      ...context,
      ...getApplicationContext(),
    };

    faro.api.pushError(error, {
      type: context.type,
      context: sanitizeContext(enrichedContext),
    });

    trackFaroIntegrationSuccess(context.errorSource);
  } catch (faroError) {
    trackFaroIntegrationFailure('faro_push_error_failed', {
      faroErrorMessage: faroError instanceof Error ? faroError.message : String(faroError),
      originalErrorMessage: error.message,
      errorSource: context.errorSource,
    });
    if (import.meta.env.DEV) {
      console.error('[FaroErrorReporter] Failed to report to Faro:', faroError);
    }
  }
}

/**
 * Extracts application context fields from Logger.appMetadata
 * This reuses the EXACT same data source as Splunk logging for perfect parity
 */
function getApplicationContext(): Record<string, unknown> {
  try {
    const appMetadata = Logger.getAppMetadata();

    return {
      // Application metadata (same as Splunk)
      app_name: appMetadata.name,
      app_version: appMetadata.version,
      client: appMetadata.client,
      pkg: appMetadata.pkg,
      user: appMetadata.userId,
      sessionId: sessionStorage.getItem(SESSION_ID_STORAGE_KEY) || undefined,
      // Location context
      url: window.location.href,
      feature: extractFeatureFromUrl(),
      previousUrl: document.referrer || undefined,
      // Browser context
      userAgent: navigator.userAgent,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      online: navigator.onLine,
      // Performance context
      memoryUsage: getMemoryUsage(),
      // Timing
      timestamp: new Date().toISOString(),
    };
  } catch (contextError) {
    trackFaroIntegrationFailure('faro_context_error', {
      contextErrorMessage: contextError instanceof Error ? contextError.message : String(contextError),
    });
    return {};
  }
}

/**
 * Sanitizes context to prevent serialization issues.
 */
function sanitizeContext(context: Record<string, unknown>): Record<string, string> {
  const MAX_STRING_LENGTH = 1000;
  const sanitized: Record<string, string> = {};

  for (const [key, value] of Object.entries(context)) {
    if (value === undefined || value === null) {
      continue;
    }

    if (typeof value === 'string') {
      sanitized[key] =
        value.length > MAX_STRING_LENGTH ? value.substring(0, MAX_STRING_LENGTH) + '...[truncated]' : value;
    } else if (typeof value === 'boolean' || typeof value === 'number') {
      sanitized[key] = String(value);
    } else {
      try {
        sanitized[key] = JSON.stringify(value);
      } catch {
        sanitized[key] = '[object]';
      }
    }
  }

  return sanitized;
}

/**
 * Extracts feature name from URL pathname (first segment).
 */
function extractFeatureFromUrl(): string | undefined {
  try {
    const segments = window.location.pathname.split('/').filter(Boolean);
    return segments.length > 0 ? segments[0] : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Gets memory usage information if available (Chrome/Edge only).
 * Returns formatted string like "245/512MB" or undefined if not supported.
 */
function getMemoryUsage(): string | undefined {
  try {
    // @ts-expect-error - performance.memory is non-standard but available in Chrome/Edge
    const memory = performance.memory as { usedJSHeapSize: number; jsHeapSizeLimit: number } | undefined;
    if (memory) {
      const usedMB = Math.round(memory.usedJSHeapSize / 1048576);
      const totalMB = Math.round(memory.jsHeapSizeLimit / 1048576);
      return `${usedMB}/${totalMB}MB`;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

/**
 * Tracks Faro integration failures to Splunk.
 */
function trackFaroIntegrationFailure(
  failureType: 'faro_api_unavailable' | 'faro_push_error_failed' | 'faro_context_error',
  metadata: Record<string, unknown>
): void {
  try {
    Logger.trackEvent({
      action: LogAction.FARO_INTEGRATION_FAILURE,
      elementInfo: {
        eventType: EventType.EVENT_ERROR,
        metadata: JSON.stringify({
          failureType,
          timestamp: new Date().toISOString(),
          ...metadata,
        }),
      },
    });
  } catch {
    if (import.meta.env.DEV) {
      console.error('[FaroErrorReporter] Failed to track failure');
    }
  }
}

/**
 * Tracks successful Faro reports to Splunk (10% sample rate).
 */
function trackFaroIntegrationSuccess(errorSource?: string): void {
  try {
    if (Math.random() > 0.1) {
      return;
    }

    Logger.trackEvent({
      action: LogAction.FARO_INTEGRATION_SUCCESS,
      elementInfo: {
        eventType: EventType.EVENT_LOAD,
        metadata: JSON.stringify({
          errorSource,
          timestamp: new Date().toISOString(),
        }),
      },
    });
  } catch {
    // Silent failure for non-critical telemetry
  }
}
